import { toast } from '@/hooks/use-toast.ts';
import { pdkAxios } from '@/lib/pdk-axios';
import { taskStore } from '@/models/stores/tasks';
import { taskTemplateStore } from '@/models/stores/tasks-v2.ts';
import type {
  TaskableType,
  TaskTemplate,
  Task as TaskType,
} from '@/models/types';
import { useMutation } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';
import {
  useAssignTaskMutation,
  useAssignTaskMutationV2,
} from './use-assign-task';

export const useCreateTaskMutation = () => {
  const assignTaskMutation = useAssignTaskMutation();

  return useMutation({
    onMutate: async ({ task }: { task: TaskType }) => {
      taskStore.add(task);
    },
    mutationFn: async ({ task }: { task: TaskType; dealId: string }) => {
      const response = await pdkAxios
        .post(`/tasks/create`, {
          id: task.id,
          title: task.title,
          description: task.description,
        })
        .catch((e) => {
          console.error('Error creating task', e);
          throw e;
        });
      return response.data;
    },
    onSuccess: (_data, { task, dealId }) => {
      // After task is created, assign it to the deal using the same mutation
      const assignedTaskId = uuidv4();

      console.log('assignTaskMutation task', task);
      assignTaskMutation.mutate(
        {
          task: {
            id: assignedTaskId,
            task_id: task.id,
            deal_id: dealId,
            title: task.title,
            description: task.description,
            status: 'incomplete',
          },
          dealId: dealId,
        },
        {
          onError: (error) => {
            console.error('Failed to assign task from suggestion', error);
          },
        },
      );
    },
    onError: (error, { task }) => {
      console.error('Failed to create task', error);
      taskStore.removeTask(task.id);
    },
  });
};

// -- generalized version

export const useCreateTaskTemplateMutation = () => {
  const assignTaskMutationV2 = useAssignTaskMutationV2();
  // TODO -- remove later
  const assignTaskMutation = useAssignTaskMutation();

  return useMutation({
    onMutate: async ({ taskTemplate }: { taskTemplate: TaskTemplate }) => {
      taskTemplateStore.add(taskTemplate);
    },
    mutationFn: async ({
      taskTemplate,
    }: {
      taskTemplate: TaskTemplate;
      taskableType: TaskableType;
      taskableId?: string;
    }) => {
      const response = await pdkAxios
        .post(`/tasks/v2/create`, {
          id: taskTemplate.id,
          title: taskTemplate.title,
          description: taskTemplate.description,
          taskable_type: taskTemplate.taskable_type,
        })
        .catch((e) => {
          console.error('Error creating task template', e);
          throw e;
        });

      // TODO -- old deal task creation, remove later
      const _ = await pdkAxios
        .post(`/tasks/create`, {
          id: taskTemplate.id,
          title: taskTemplate.title,
          description: taskTemplate.description,
        })
        .catch((e) => {
          console.error('Error creating deal task', e);
          throw e;
        });
      return response.data + ' ' + _.data;
    },
    onSuccess: (_data, { taskTemplate, taskableType, taskableId }) => {
      if (!taskableId) {
        return;
      }

      const taskId = uuidv4();

      assignTaskMutationV2.mutate(
        {
          task: {
            id: taskId,
            task_template_id: taskTemplate.id,
            taskable_type: taskableType,
            taskable_id: taskableId,
            title: taskTemplate.title,
            description: taskTemplate.description,
            status: 'incomplete',
          },
          taskableId,
          taskableType,
        },
        {
          onError: (error) => {
            console.error('Failed to assign task from suggestion', error);
          },
        },
      );

      // TODO -- old task assign, remove later
      assignTaskMutation.mutate(
        {
          task: {
            id: taskId,
            task_id: taskTemplate.id,
            deal_id: taskableId,
            title: taskTemplate.id,
            description: taskTemplate.description,
            status: 'incomplete',
          },
          dealId: taskableId,
        },
        {
          onError: (error) => {
            console.error('Failed to assign task from suggestion', error);
          },
        },
      );
    },
    onError: (error: any, { taskTemplate }) => {
      const status = error?.response?.status;
      taskTemplateStore.removeTaskTemplate(taskTemplate.id);

      if (status === 409) {
        toast({
          variant: 'destructive',
          title: 'Duplicate task template title',
          description:
            'A task template with this title already exists. Please choose a different title.',
        });
        return;
      }

      console.error('Failed to create task template', error);
      toast({
        variant: 'destructive',
        title: 'Could not create task template',
        description: 'Something went wrong while saving your task template.',
      });
    },
  });
};
